/**
 * client/src/pages/security-centre/components/credential-held-notice.tsx
 *
 * The honest surface for a passkey HELD after an incident (Stage 7 "passkey
 * boundary gaps", module 02, Task 2, AC-8, #3099). When a Forensic Pause lifts,
 * the keys its incident named stay held until the owner clears them with a
 * DIFFERENT passkey — or removes them.
 *
 * AMBER, NEVER RED. The pause is over, so this is not Forensic Pause red; and
 * nothing the person did was an error. It is a standing state waiting on them.
 *
 * TWO CONTROLS, ONE PER REAL ROUTE:
 *   - "Clear with another passkey" — the fresh-UV ceremony against
 *     `/clear-incident-hold` (begin → passkey prompt → complete), reusing the
 *     shared begin/assert/complete runner.
 *   - "Remove" — the existing removal, unchanged (its own ceremony and holds).
 * A held MASTER cannot be cleared that way (the master pin would demand the
 * held key), so its first control is a link to the master transfer flow on
 * this page instead.
 *
 * Copy claims possession only — never who used the key (§Friction Rule clause 2).
 */

import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import type { AuthenticationResponseJSON } from "@simplewebauthn/browser";

import { invalidateHardwareKeyState } from "@/lib/webauthn-credential-invalidation";
import { beginClearIncidentHold, completeClearIncidentHold } from "../master/api";
import { useMasterCeremony } from "../master/use-master-ceremony";

/** In-page anchor on MasterPanel's active branch (the transfer entry point). */
export const MASTER_PANEL_ANCHOR = "master-passkey";

interface CredentialHeldNoticeProps {
  /** WebAuthn `credentialId` — the id the credential routes address. */
  credentialId: string;
  isMaster: boolean;
  onRemove: (credentialId: string) => void;
  removeDisabled: boolean;
}

export function CredentialHeldNotice({
  credentialId,
  isMaster,
  onRemove,
  removeDisabled,
}: CredentialHeldNoticeProps): JSX.Element {
  const queryClient = useQueryClient();
  const begin = useCallback(() => beginClearIncidentHold(credentialId), [credentialId]);
  const complete = useCallback(
    (response: AuthenticationResponseJSON) => completeClearIncidentHold(credentialId, response),
    [credentialId],
  );
  const ceremony = useMasterCeremony(begin, complete);

  const clear = async (): Promise<void> => {
    const result = await ceremony.run();
    if (result) invalidateHardwareKeyState(queryClient);
  };

  const controlClass =
    "min-h-[44px] px-2 text-xs font-medium text-amber-300 underline underline-offset-2 disabled:opacity-40";

  return (
    <div
      data-testid={`credential-held-notice-${credentialId}`}
      className="mt-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2"
    >
      <p className="flex items-start gap-1.5 text-xs text-amber-200">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-400" aria-hidden="true" />
        <span>
          {isMaster
            ? "Held after an incident. Move master to another passkey, then clear this one with it."
            : "Held after an incident. Clear it with another passkey, or remove it."}
        </span>
      </p>
      <div className="mt-1 flex flex-wrap items-center gap-2">
        {isMaster ? (
          <a href={`#${MASTER_PANEL_ANCHOR}`} className={controlClass}>
            Move master to another passkey first
          </a>
        ) : (
          <button type="button" onClick={() => void clear()} disabled={ceremony.isRunning} className={controlClass}>
            Clear with another passkey
          </button>
        )}
        {/* A held MASTER can't be removed here: removal needs the master's own
            fresh check, which a held key fails. Move master first. */}
        {!isMaster && (
          <button
            type="button"
            onClick={() => onRemove(credentialId)}
            disabled={removeDisabled}
            className={controlClass}
          >
            Remove
          </button>
        )}
      </div>
      {ceremony.error && (
        <p role="alert" className="mt-1 text-xs text-amber-300">{ceremony.error}</p>
      )}
    </div>
  );
}
